// @ts-check
import {DragDropProvider} from '@dnd-kit/react'
import {Skeleton} from '@mui/material'
import {Text} from '@/components-v2'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import {BrandItem} from './BrandItem'
import {MAX_BRANDS_TO_DISPLAY} from '@/containers/Content/ProductCatalog/utils/productCatalogUtils'
import {BRAND_SIZE, LIMIT_BRANDS_SCROLL} from '../utils/catalogLayoutConstants'

const BRAND_GAP = 32
const BRAND_STRIDE = BRAND_SIZE + BRAND_GAP
const OVERSCAN = 3

/**
 * @param {{
 *   brands: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *   setBrands: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategoryBrand[]>>,
 *   isLoading: boolean,
 *   onDragEnd: (event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   disabled?: boolean,
 *   onDraggingChange: (dragging: boolean) => void,
 *   onBrandBusyChange: (id: string, busy: boolean) => void,
 * }} props
 */
export const BrandsSection = ({
  brands: allBrands,
  setBrands,
  isLoading,
  onDragEnd,
  disabled,
  onDraggingChange,
  onBrandBusyChange,
}) => {
  // Only the strip is limited. The full list is still kept in state and published.
  const brands = LIMIT_BRANDS_SCROLL ? allBrands.slice(0, MAX_BRANDS_TO_DISPLAY) : allBrands
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null))
  const [range, setRange] = useState({start: 0, end: 6})
  const [retainedIds, setRetainedIds] = useState(() => new Set())
  const [draggedId, setDraggedId] = useState(/** @type {import('@dnd-kit/abstract').UniqueIdentifier | null} */ (null))
  const [focusedId, setFocusedId] = useState(/** @type {string | null} */ (null))

  const updateRange = useCallback(() => {
    const element = scrollRef.current
    if (!element) return
    const start = Math.max(0, Math.floor(element.scrollLeft / BRAND_STRIDE) - OVERSCAN)
    const end = Math.ceil((element.scrollLeft + element.clientWidth) / BRAND_STRIDE) + OVERSCAN
    setRange(previous => (previous.start === start && previous.end === end ? previous : {start, end}))
  }, [])

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element) return
    updateRange()
    const observer = new ResizeObserver(updateRange)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isLoading, brands.length, updateRange])

  const onRetentionChange = useCallback((id, retained) => {
    setRetainedIds(previous => {
      if (previous.has(id) === retained) return previous
      const next = new Set(previous)
      if (retained) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const renderedIndices = []
  brands.forEach((brand, index) => {
    if (
      (index >= range.start && index < range.end) ||
      retainedIds.has(brand.id) ||
      brand.id === draggedId ||
      brand.id === focusedId
    ) {
      renderedIndices.push(index)
    }
  })

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6">
      <div className="flex items-center justify-between">
        <Text headingLevel={5}>Brands</Text>
        <Text>{`Max ${MAX_BRANDS_TO_DISPLAY}`}</Text>
      </div>
      {isLoading ? (
        <div className="flex gap-8 overflow-x-auto" style={{contain: 'layout paint'}}>
          {Array.from({length: 6}, (_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              className="rounded-lg"
              style={{minWidth: BRAND_SIZE}}
              width={BRAND_SIZE}
              height={BRAND_SIZE}
            />
          ))}
        </div>
      ) : (
        <DragDropProvider
          onDragStart={event => {
            setDraggedId(event.operation.source?.id ?? null)
            onDraggingChange(true)
          }}
          onDragEnd={event => {
            onDragEnd(event)
            setDraggedId(null)
            onDraggingChange(false)
          }}
        >
          <div
            ref={scrollRef}
            className="flex overflow-x-auto"
            onScroll={updateRange}
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) setFocusedId(null)
            }}
          >
            {renderedIndices.map((index, renderedIndex) => {
              const brand = brands[index]
              const previousIndex = renderedIndices[renderedIndex - 1] ?? -1
              return (
                <div key={brand.id} className="flex shrink-0">
                  {index > previousIndex + 1 && (
                    <div className="shrink-0" style={{width: (index - previousIndex - 1) * BRAND_STRIDE}} />
                  )}
                  <div
                    className="flex shrink-0 gap-8"
                    style={{
                      width: BRAND_SIZE,
                      marginRight: index < brands.length - 1 ? BRAND_GAP : 0,
                    }}
                    onFocusCapture={() => setFocusedId(brand.id)}
                  >
                    <BrandItem
                      brand={brand}
                      position={index + 1}
                      setBrands={setBrands}
                      disabled={disabled}
                      onBusyChange={onBrandBusyChange}
                      onRetentionChange={onRetentionChange}
                    />
                  </div>
                </div>
              )
            })}
            {renderedIndices.length > 0 && renderedIndices[renderedIndices.length - 1] < brands.length - 1 && (
              <div
                className="shrink-0"
                style={{
                  width: (brands.length - 1 - renderedIndices[renderedIndices.length - 1]) * BRAND_STRIDE - BRAND_GAP,
                }}
              />
            )}
          </div>
        </DragDropProvider>
      )}
    </div>
  )
}
